import { getArticleBrand } from "~/utils/articleBrand";

// Helper: construye los campos enriquecidos (medida, referencia del proveedor,
// MARCA, precio anidado y estructuras relacionadas) compartidos por addItemToCart
// y updateItemProvider. Los items que vienen del API exponen estos datos bajo
// `item.item.*` y a nivel top-level; los getters de las tablas y de la vista
// previa del email leen esa misma forma. Cuando construimos/mutamos un item del
// carrito "a mano" debemos replicarla o el precio sale a 0, la medida vacía y la
// referencia (o la marca, intranet-014) desaparece o queda obsoleta
// (incidencias #1, #2 y #3 de la ORDEN 38).
//
// Usa `||` (no `??`) deliberadamente: una cadena vacía debe degradar al
// siguiente fallback, nunca conservarse sobre un valor real. Las estructuras
// anidadas (purchase_unit_details, article_reference) se propagan SOLO si son
// truthy: nunca se fabrica `{}`, porque los getters esperan array/objeto con
// `.description`/`.name`/`.category` o caen a su propio fallback.
function buildEnrichedItemFields({
	articlePrice,
	unitPrice,
	fallbackPurchaseUnit,
	oldItem,
} = {}) {
	const ap = articlePrice || {};
	const old = oldItem || {};

	// Referencia del PROVEEDOR (p.ej. 10080), no el código interno del artículo.
	const referenceCode =
		ap.reference_code ||
		ap.Reference?.code ||
		old.reference_code ||
		old.item?.reference_code ||
		"";

	// Medida / unidad de COMPRA descriptiva (lo que pinta la vista previa y el correo).
	// Regla de negocio: en listados y correos se muestra la medida de COMPRA
	// (purchase_unit_description), NUNCA la de inventario (conversion_measure_description).
	const measure =
		ap.purchase_unit_description ||
		ap.purchase_unit_details?.description ||
		ap.purchase_unit ||
		fallbackPurchaseUnit ||
		old.item?.purchase_unit_description ||
		old.item?.purchase_unit ||
		old.purchase_unit ||
		"";

	// purchase_unit a nivel top-level (p.ej. "Caja", "Kg").
	const purchaseUnit =
		ap.purchase_unit ||
		fallbackPurchaseUnit ||
		old.purchase_unit ||
		measure ||
		"";

	// Estructuras anidadas: propagar tal cual SOLO si son truthy.
	const purchaseUnitDetails =
		ap.purchase_unit_details ||
		old.item?.purchase_unit_details ||
		old.purchase_unit_details ||
		undefined;

	// Marca (article_references.category, intranet-014): la del article_price
	// NUEVO si la trae (category plano del DTO de /article-prices/article/:id o
	// article_reference anidado); si no trae ninguna, se conserva la del item
	// viejo (semántica || compartida: vacío degrada).
	const apBrand = getArticleBrand(ap);
	const brand = apBrand || getArticleBrand(old);

	// article_reference del proveedor viejo SOLO se propaga si el ap nuevo no
	// aporta marca propia: getArticleBrand da precedencia a article_reference
	// sobre category, así que conservarla dejaría la marca vieja ganando a la
	// nueva (exactamente el bug de marca obsoleta que este campo evita).
	const articleReference =
		ap.article_reference ||
		(apBrand
			? undefined
			: old.item?.article_reference || old.article_reference) ||
		undefined;

	// Campos anidados para `item:{}` (los getters leen item.item.*).
	// `measure` es la medida de COMPRA; se guarda en purchase_unit_description (campo
	// canónico que leen los helpers) y se mantiene conversion_measure_description por
	// compatibilidad con lectores antiguos (mismo valor de compra).
	const itemFields = {
		price: unitPrice,
		reference_code: referenceCode,
		purchase_unit_description: measure,
		conversion_measure_description: measure,
		purchase_unit: purchaseUnit,
	};
	// Campos top-level (fallback de los getters cuando item.item.* no existe).
	const topLevel = {
		reference_code: referenceCode,
		purchase_unit: purchaseUnit,
	};

	if (purchaseUnitDetails) {
		itemFields.purchase_unit_details = purchaseUnitDetails;
		topLevel.purchase_unit_details = purchaseUnitDetails;
	}
	if (articleReference) {
		itemFields.article_reference = articleReference;
		topLevel.article_reference = articleReference;
	} else if (apBrand) {
		// El ap nuevo trae marca propia sin estructura article_reference: hay que
		// PISAR la del proveedor anterior en el spread ({...old.item, ...itemFields})
		// o seguiría resolviéndose la marca obsoleta.
		itemFields.article_reference = undefined;
		topLevel.article_reference = undefined;
	}
	if (brand) {
		itemFields.category = brand;
		topLevel.category = brand;
	}

	return { topLevel, itemFields };
}

export default {
	namespaced: true,

	state: {
		// Solicitudes de pedido disponibles (filtradas por estado)
		availableRequests: [],

		// Solicitudes seleccionadas para el carrito (array of IDs)
		selectedRequestIds: [],

		// Items de las solicitudes seleccionadas (cargados on-demand)
		requestItems: [],

		// Items agrupados por proveedor
		groupedByProvider: {},

		// Metadatos del carrito
		cartMetadata: {
			totalItems: 0,
			totalAmount: 0,
			providersCount: 0,
		},

		// Observaciones generales del pedido
		generalObservations: '',

		// Observaciones por proveedor (map providerId → string)
		providerObservations: {},

		// Estados de loading
		loading: {
			requests: false,
			items: false,
			providers: false,
			submit: false,
		},

		// Errores
		errors: {
			fetchRequests: null,
			fetchItems: null,
			groupByProvider: null,
			submitOrder: null,
		},

		// OPTIMIZATION: Cache for computed values
		_cache: {
			selectedRequests: null,
			providersArray: null,
		},
	},

	getters: {
		// Solicitudes disponibles (filtradas por estado)
		getAvailableRequests: (state) => state.availableRequests,

		// OPTIMIZATION: Memoized selected requests
		getSelectedRequests: (state) => {
			// Return cached value if available and state hasn't changed
			if (state._cache.selectedRequests &&
				state._cache.selectedRequestsIds === state.selectedRequestIds) {
				return state._cache.selectedRequests;
			}

			// Compute and cache
			const selected = state.availableRequests.filter((req) =>
				state.selectedRequestIds.includes(req.ID)
			);

			return selected;
		},

		// IDs de solicitudes seleccionadas
		getSelectedRequestIds: (state) => state.selectedRequestIds,

		// Items del carrito
		getCartItems: (state) => state.requestItems,

		// Items agrupados por proveedor
		getGroupedByProvider: (state) => state.groupedByProvider,

		// Total del carrito (formatted)
		getCartTotal: (state) => state.cartMetadata.totalAmount,

		// Count de items
		getCartItemsCount: (state) => state.cartMetadata.totalItems,

		// Count de proveedores
		getProvidersCount: (state) => state.cartMetadata.providersCount,

		// Loading states
		isLoading: (state) => Object.values(state.loading).some((v) => v),

		isLoadingRequests: (state) => state.loading.requests,
		isLoadingItems: (state) => state.loading.items,
		isLoadingProviders: (state) => state.loading.providers,
		isSubmitting: (state) => state.loading.submit,

		// Validaciones
		isCartEmpty: (state) => state.selectedRequestIds.length === 0,
		hasErrors: (state) => Object.values(state.errors).some((e) => e !== null),

		// Obtener un proveedor específico por ID
		getProviderById: (state) => (providerId) => {
			return state.groupedByProvider[providerId] || null;
		},

		// OPTIMIZATION: Memoized providers array
		getProvidersArray: (state) => {
			// Return cached value if available
			if (state._cache.providersArray) {
				return state._cache.providersArray;
			}

			// Compute and cache
			const providers = Object.values(state.groupedByProvider);
			return providers;
		},

		// OPTIMIZATION: Computed property for formatted totals
		getFormattedTotals: (state) => {
			return {
				totalItems: state.cartMetadata.totalItems,
				totalAmount: state.cartMetadata.totalAmount.toFixed(2),
				providersCount: state.cartMetadata.providersCount,
			};
		},

		// Observaciones generales del pedido
		getGeneralObservations: (state) => state.generalObservations,

		// Observaciones por proveedor (map providerId → string)
		getProviderObservations: (state) => state.providerObservations,
	},

	mutations: {
		// SET_AVAILABLE_REQUESTS
		SET_AVAILABLE_REQUESTS(state, requests) {
			state.availableRequests = requests;
			// OPTIMIZATION: Invalidate cache
			state._cache.selectedRequests = null;
		},

		// ADD_REQUEST_TO_CART
		ADD_REQUEST_TO_CART(state, requestId) {
			if (!state.selectedRequestIds.includes(requestId)) {
				state.selectedRequestIds.push(requestId);
			}
			// OPTIMIZATION: Invalidate cache
			state._cache.selectedRequests = null;
		},

		// REMOVE_REQUEST_FROM_CART
		REMOVE_REQUEST_FROM_CART(state, requestId) {
			state.selectedRequestIds = state.selectedRequestIds.filter(
				(id) => id !== requestId
			);
			// OPTIMIZATION: Invalidate cache
			state._cache.selectedRequests = null;
		},

		// CLEAR_CART
		CLEAR_CART(state) {
			state.selectedRequestIds = [];
			state.requestItems = [];
			state.groupedByProvider = {};
			state.cartMetadata = {
				totalItems: 0,
				totalAmount: 0,
				providersCount: 0,
			};
			state.generalObservations = '';
			state.providerObservations = {};
			// OPTIMIZATION: Invalidate all cache
			state._cache.selectedRequests = null;
			state._cache.providersArray = null;
		},

		// SET_GENERAL_OBSERVATIONS
		SET_GENERAL_OBSERVATIONS(state, observations) {
			state.generalObservations = observations;
		},

		// SET_PROVIDER_OBSERVATIONS - reemplaza el mapa completo
		SET_PROVIDER_OBSERVATIONS(state, observations) {
			state.providerObservations = { ...observations };
		},

		// SET_PROVIDER_OBSERVATION - actualiza la nota de un proveedor concreto
		SET_PROVIDER_OBSERVATION(state, { providerId, observation }) {
			// Vue 2: usar Vue.set vía spread para mantener reactividad
			state.providerObservations = {
				...state.providerObservations,
				[providerId]: observation,
			};
		},

		// SET_REQUEST_ITEMS
		SET_REQUEST_ITEMS(state, items) {
			state.requestItems = items;
		},

		// SET_GROUPED_BY_PROVIDER
		SET_GROUPED_BY_PROVIDER(state, grouped) {
			state.groupedByProvider = grouped;
			// OPTIMIZATION: Invalidate cache
			state._cache.providersArray = null;
		},

		// UPDATE_CART_METADATA
		UPDATE_CART_METADATA(state, metadata) {
			state.cartMetadata = { ...state.cartMetadata, ...metadata };
		},

		// SET_LOADING
		SET_LOADING(state, { key, value }) {
			if (state.loading.hasOwnProperty(key)) {
				state.loading[key] = value;
			}
		},

		// SET_ERROR
		SET_ERROR(state, { key, error }) {
			if (state.errors.hasOwnProperty(key)) {
				state.errors[key] = error;
			}
		},

		// CLEAR_ERRORS
		CLEAR_ERRORS(state) {
			Object.keys(state.errors).forEach((key) => {
				state.errors[key] = null;
			});
		},
	},

	actions: {
		// fetchAvailableRequests
		async fetchAvailableRequests({ commit }) {
			commit("SET_LOADING", { key: "requests", value: true });
			commit("SET_ERROR", { key: "fetchRequests", error: null });

			try {
				// SECURITY: Validate token before request
				const token = window.localStorage.getItem("auth._token.local");
				if (!token) {
					throw new Error("No authentication token found");
				}

				const response = await this.$axios.get("/order-requests", {
					params: { state: "en_revision" },
					headers: {
						Authorization: token,
					},
				});

				// SECURITY: Validate response structure
				if (!response.data) {
					throw new Error("Invalid response from server");
				}

				const requests = response.data.data || response.data;

				// SECURITY: Validate requests is an array
				if (!Array.isArray(requests)) {
					throw new Error("Invalid requests data format");
				}

				commit("SET_AVAILABLE_REQUESTS", requests);
				commit("SET_LOADING", { key: "requests", value: false });

				return requests;
			} catch (error) {
				// SECURITY: Handle auth errors (401, 403)
				if (error.response && (error.response.status === 401 || error.response.status === 403)) {
					// Token expired or invalid - redirect to login
					window.location.href = "/login";
					throw new Error("Sesión expirada. Por favor, inicia sesión nuevamente.");
				}

				commit("SET_ERROR", {
					key: "fetchRequests",
					error: error.message,
				});
				commit("SET_LOADING", { key: "requests", value: false });
				throw error;
			}
		},

		// fetchRequestItems
		async fetchRequestItems({ commit, state }) {
			if (state.selectedRequestIds.length === 0) {
				commit("CLEAR_CART");
				return [];
			}

			// SECURITY: Validate request IDs
			const invalidIds = state.selectedRequestIds.filter(id =>
				!id || typeof id !== 'number' || id <= 0
			);
			if (invalidIds.length > 0) {
				throw new Error("Invalid request IDs detected");
			}

			commit("SET_LOADING", { key: "items", value: true });
			commit("SET_ERROR", { key: "fetchItems", error: null });

			try {
				// SECURITY: Validate token before request
				const token = window.localStorage.getItem("auth._token.local");
				if (!token) {
					throw new Error("No authentication token found");
				}

				// Fetch items para cada solicitud seleccionada
				const itemsPromises = state.selectedRequestIds.map((requestId) =>
					this.$axios.get("/order-request-items", {
						params: { order_request_id: requestId },
						headers: {
							Authorization: token,
						},
					})
				);

				const responses = await Promise.all(itemsPromises);
				const allItems = responses.flatMap(
					(r) => r.data.data || r.data
				);

				// SECURITY: Validate items is an array
				if (!Array.isArray(allItems)) {
					throw new Error("Invalid items data format");
				}

				commit("SET_REQUEST_ITEMS", allItems);

				// Agrupar items por proveedor
				await dispatch("groupByProvider", allItems);

				commit("SET_LOADING", { key: "items", value: false });

				return allItems;
			} catch (error) {
				// SECURITY: Handle auth errors
				if (error.response && (error.response.status === 401 || error.response.status === 403)) {
					window.location.href = "/login";
					throw new Error("Sesión expirada. Por favor, inicia sesión nuevamente.");
				}

				commit("SET_ERROR", {
					key: "fetchItems",
					error: error.message,
				});
				commit("SET_LOADING", { key: "items", value: false });
				throw error;
			}
		},

		// groupByProvider
		async groupByProvider({ commit, state }, items) {
			commit("SET_LOADING", { key: "providers", value: true });
			commit("SET_ERROR", { key: "groupByProvider", error: null });

			try {
				// SECURITY: Validate items parameter
				if (!Array.isArray(items)) {
					throw new Error("Invalid items parameter");
				}

				// Agrupar items por ProviderID
				const grouped = items.reduce((acc, item, index) => {
					// SECURITY: Validate item structure
					if (!item || typeof item !== 'object') {
						return acc;
					}

					const providerId =
						item.ProviderID ||
						item.provider_id ||
						item.ArticlePrice?.ProviderID;

					if (!providerId) {
						return acc;
					}

					// SECURITY: Validate provider ID
					if (typeof providerId !== 'number' || providerId <= 0) {
						return acc;
					}

					if (!acc[providerId]) {
						const providerName =
							item.Provider?.name ||
							item.provider_name ||
							item.ArticlePrice?.Provider?.name ||
							`Proveedor ${providerId}`;

						const providerEmail =
							item.Provider?.email ||
							item.provider_email ||
							item.ArticlePrice?.Provider?.email ||
							'';

						// SECURITY: Sanitize provider name
						acc[providerId] = {
							providerId,
							providerName: String(providerName).substring(0, 255), // Limit length
							providerEmail: String(providerEmail).substring(0, 255), // Limit length
							items: [],
							totalAmount: 0,
							itemCount: 0,
						};
					}

					acc[providerId].items.push(item);

					// SECURITY: Validate and sanitize price
					const price = parseFloat(
						item.total_price ||
							item.amount ||
							item.final_price * item.quantity ||
							0
					);

					if (isNaN(price)) {
						console.warn("Invalid price for item:", item);
						return acc;
					}

					acc[providerId].totalAmount += price;
					acc[providerId].itemCount += 1;

					return acc;
				}, {});

				commit("SET_GROUPED_BY_PROVIDER", grouped);

				// Calcular totales del carrito
				const totalItems = items.length;
				const totalAmount = Object.values(grouped).reduce(
					(sum, group) => sum + group.totalAmount,
					0
				);
				const providersCount = Object.keys(grouped).length;

				commit("UPDATE_CART_METADATA", {
					totalItems,
					totalAmount,
					providersCount,
				});

				commit("SET_LOADING", { key: "providers", value: false });

				return grouped;
			} catch (error) {
				commit("SET_ERROR", {
					key: "groupByProvider",
					error: error.message,
				});
				commit("SET_LOADING", { key: "providers", value: false });
				throw error;
			}
		},

		// addRequestToCart
		async addRequestToCart({ dispatch, commit }, requestId) {
			// SECURITY: Validate requestId parameter
			if (!requestId || typeof requestId !== 'number' || requestId <= 0) {
				throw new Error("Invalid request ID");
			}

			commit("ADD_REQUEST_TO_CART", requestId);
			await dispatch("fetchRequestItems");
		},

		// removeRequestFromCart
		async removeRequestFromCart({ dispatch, commit }, requestId) {
			// SECURITY: Validate requestId parameter
			if (!requestId || typeof requestId !== 'number' || requestId <= 0) {
				throw new Error("Invalid request ID");
			}

			commit("REMOVE_REQUEST_FROM_CART", requestId);
			await dispatch("fetchRequestItems");
		},

		// toggleRequestInCart
		async toggleRequestInCart({ dispatch, commit, state }, requestId) {
			// SECURITY: Validate requestId parameter
			if (!requestId || typeof requestId !== 'number' || requestId <= 0) {
				throw new Error("Invalid request ID");
			}

			const index = state.selectedRequestIds.indexOf(requestId);
			if (index > -1) {
				commit("REMOVE_REQUEST_FROM_CART", requestId);
			} else {
				commit("ADD_REQUEST_TO_CART", requestId);
			}
			await dispatch("fetchRequestItems");
		},

		// clearCart
		clearCart({ commit }) {
			commit("CLEAR_CART");
		},

		// submitOrder
		async submitOrder({ commit, state, dispatch }, orderData) {
			commit("SET_LOADING", { key: "submit", value: true });
			commit("SET_ERROR", { key: "submitOrder", error: null });

			try {
				// SECURITY: Validate orderData structure
				if (!orderData || typeof orderData !== 'object') {
					throw new Error("Invalid order data");
				}

				// SECURITY: Validate required fields
				// profile_id: null permitido (el API lo resolverá según el usuario)
				if (
					orderData.profile_id !== null &&
					orderData.profile_id !== undefined &&
					typeof orderData.profile_id !== "number"
				) {
					throw new Error("Invalid profile ID");
				}

				if (!orderData.establishment_id || typeof orderData.establishment_id !== 'number') {
					throw new Error("Invalid establishment ID");
				}

				if (!orderData.week_start || !orderData.week_end) {
					throw new Error("Week dates are required");
				}

				// NOTE: Date validations are now handled by the API
				// Removed frontend validation to allow API to handle business rules

				// SECURITY: Validate observations length
				if (orderData.observations && orderData.observations.length > 1000) {
					throw new Error("Las observaciones no pueden exceder 1000 caracteres");
				}

				// SECURITY: Validate token before request
				const token = window.localStorage.getItem("auth._token.local");
				if (!token) {
					throw new Error("No authentication token found");
				}

				const response = await this.$axios.post(
					"/purchase-orders",
					orderData,
					{
						headers: {
							Authorization: token,
						},
					}
				);

				// SECURITY: Validate response
				if (!response.data) {
					throw new Error("Invalid response from server");
				}

				// Limpiar carrito después de submit exitoso
				commit("CLEAR_CART");
				commit("SET_LOADING", { key: "submit", value: false });

				return response.data;
			} catch (error) {
				// SECURITY: Handle auth errors
				if (error.response && (error.response.status === 401 || error.response.status === 403)) {
					window.location.href = "/login";
					throw new Error("Sesión expirada. Por favor, inicia sesión nuevamente.");
				}

				// intranet-002: el backend manda el motivo real en
				// error.response.data.details/.error (ver handler.go:CreateOrUpdate); sin
				// response (fallo de red puro / timeout), cae al genérico de axios.
				let errorMessage =
					(error.response && error.response.data && (error.response.data.details || error.response.data.error)) ||
					error.message ||
					"Error al crear pedido";

				// SECURITY: Remove technical details from error messages
				errorMessage = errorMessage
					.replace(/\/api\/v1/g, '')
					.replace(/https?:\/\/[^\s]+/g, '')
					.replace(/\d{3,}/g, 'XXX'); // Remove long numbers (IDs)

				commit("SET_ERROR", {
					key: "submitOrder",
					error: errorMessage,
				});
				commit("SET_LOADING", { key: "submit", value: false });
				// intranet-002 (defecto real de scenario-coverage): relanzar el error
				// ORIGINAL de axios haría que CustomPurchaseOrderCart.vue (que solo lee
				// `.message`) siguiera viendo el genérico ("Request failed with status
				// code 400"), no el mensaje real ya extraído arriba. Se relanza un Error
				// NUEVO con el texto saneado.
				throw new Error(errorMessage);
			}
		},

		// initializeCart
		async initializeCart({ dispatch }) {
			try {
				// Fetch available requests
				await dispatch("fetchAvailableRequests");
			} catch (error) {
				console.error("Error initializing cart:", error);
				throw error;
			}
		},

		// updateItemProvider - Cambiar el proveedor de un item
		// Params extra OPCIONALES (retrocompatibles): newReferenceCode,
		// newPurchaseUnit y newArticlePrice (el article_price completo) permiten
		// refrescar la referencia y la medida del NUEVO proveedor (incidencias
		// #2/#3). Sin ellos, el comportamiento previo se mantiene.
		async updateItemProvider({ commit, state, dispatch }, { itemId, newProviderId, newPrice, newProviderName, newArticlePriceId, newReferenceCode, newPurchaseUnit, newArticlePrice }) {
			try {
				// SECURITY: Validate parameters
				if (!itemId || typeof itemId !== 'number') {
					throw new Error("Invalid item ID");
				}
				if (!newProviderId || typeof newProviderId !== 'number') {
					throw new Error("Invalid provider ID");
				}
				if (!newPrice || typeof newPrice !== 'number') {
					throw new Error("Invalid price");
				}
				if (!newProviderName || typeof newProviderName !== 'string') {
					throw new Error("Invalid provider name");
				}
				if (!newArticlePriceId || typeof newArticlePriceId !== 'number') {
					throw new Error("Invalid article price ID");
				}

				// Buscar el item en requestItems
				const itemIndex = state.requestItems.findIndex(item => item.ID === itemId);
				if (itemIndex === -1) {
					throw new Error("Item not found in cart");
				}

				// Clonar el array de items para mutación inmutable
				const updatedItems = [...state.requestItems];
				const oldItem = updatedItems[itemIndex];

				// Resolver los campos enriquecidos del NUEVO proveedor. Si no se
				// recibe el article_price completo, sintetizamos uno con los
				// escalares disponibles; el helper hace fallback a oldItem para no
				// borrar datos correctos cuando el nuevo valor sea ausente.
				const effectiveArticlePrice = newArticlePrice || {
					reference_code: newReferenceCode,
					purchase_unit: newPurchaseUnit,
				};
				const { topLevel, itemFields } = buildEnrichedItemFields({
					articlePrice: effectiveArticlePrice,
					unitPrice: newPrice,
					fallbackPurchaseUnit: newPurchaseUnit,
					oldItem,
				});

				// Actualizar el item con el nuevo proveedor
				updatedItems[itemIndex] = {
					...oldItem,
					provider_id: newProviderId,
					ProviderID: newProviderId,
					article_price_id: newArticlePriceId,
					ArticlePriceID: newArticlePriceId,
					unit_price: newPrice,
					total_price: newPrice * oldItem.quantity,
					...topLevel,
					// Actualizar el item.item con el nuevo proveedor + datos enriquecidos
					item: {
						...oldItem.item,
						provider: newProviderName,
						...itemFields
					},
					// Actualizar Provider si existe
					Provider: oldItem.Provider ? {
						...oldItem.Provider,
						ID: newProviderId,
						name: newProviderName
					} : {
						ID: newProviderId,
						name: newProviderName
					}
				};

				// Actualizar requestItems en el estado
				commit("SET_REQUEST_ITEMS", updatedItems);

				// Re-agrupar por proveedor
				await dispatch("groupByProvider", updatedItems);

				return updatedItems[itemIndex];
			} catch (error) {
				console.error("❌ Error updating item provider:", error);
				throw error;
			}
		},

		// fetchAlternativeProviders - Obtener proveedores alternativos para un artículo
		async fetchAlternativeProviders({ commit }, articleId) {
			try {
				// SECURITY: Validate articleId
				if (!articleId || typeof articleId !== 'number') {
					throw new Error("Invalid article ID");
				}

				// SECURITY: Validate token before request
				const token = window.localStorage.getItem("auth._token.local");
				if (!token) {
					throw new Error("No authentication token found");
				}

				// Llamar al endpoint correcto: /article-prices/article/:article_id
				const response = await this.$axios.get(`/article-prices/article/${articleId}`, {
					headers: {
						Authorization: token,
					},
				});

				// SECURITY: Validate response structure
				if (!response.data) {
					throw new Error("Invalid response from server");
				}

				const providers = response.data.data || response.data;

				// SECURITY: Validate providers is an array
				if (!Array.isArray(providers)) {
					throw new Error("Invalid providers data format");
				}

				return providers;
			} catch (error) {
				// SECURITY: Handle auth errors
				if (error.response && (error.response.status === 401 || error.response.status === 403)) {
					window.location.href = "/login";
					throw new Error("Sesión expirada. Por favor, inicia sesión nuevamente.");
				}

				throw error;
			}
		},

		/**
		 * Actualiza la cantidad de un item del carrito.
		 *
		 * Acepta cantidades NEGATIVAS (retiradas/recogidas al proveedor); el `0` sigue
		 * prohibido (para quitar una línea existe `removeItem`). El guard conserva
		 * `!newQuantity`, que ya rechaza `0`/`NaN`, tras retirar el antiguo `<= 0`
		 * (intranet-008). El importe (`unit_price * newQuantity`) conserva el signo.
		 *
		 * @param {{ itemId:number, newQuantity:number }} payload
		 * @returns {Object} El item actualizado.
		 */
		async updateItemQuantity({ commit, state, dispatch }, { itemId, newQuantity }) {
			try {
				// SECURITY: Validate parameters
				if (!itemId || typeof itemId !== 'number') {
					throw new Error("Invalid item ID");
				}
				if (!newQuantity || typeof newQuantity !== 'number') {
					throw new Error("Invalid quantity. Must be a non-zero number");
				}

				// Buscar el item en requestItems
				const itemIndex = state.requestItems.findIndex(item => item.ID === itemId);
				if (itemIndex === -1) {
					throw new Error("Item not found in cart");
				}

				// Clonar el array de items para mutación inmutable
				const updatedItems = [...state.requestItems];
				const oldItem = updatedItems[itemIndex];

				// Actualizar el item con la nueva cantidad
				updatedItems[itemIndex] = {
					...oldItem,
					quantity: newQuantity,
					total_price: oldItem.unit_price * newQuantity
				};

				// Actualizar requestItems en el estado
				commit("SET_REQUEST_ITEMS", updatedItems);

				// Re-agrupar por proveedor
				await dispatch("groupByProvider", updatedItems);

				return updatedItems[itemIndex];
			} catch (error) {
				console.error("❌ Error updating item quantity:", error);
				throw error;
			}
		},

		/**
		 * Añade un nuevo artículo al carrito (sin llamada API adicional).
		 *
		 * Acepta cantidades NEGATIVAS (retiradas); el `0` sigue prohibido. El guard conserva
		 * `!quantity` (rechaza `0`/`NaN`) tras retirar el antiguo `<= 0` (intranet-008). El
		 * importe (`unitPrice * quantity`) conserva el signo.
		 *
		 * @returns {Object} El item recién creado.
		 */
		async addItemToCart({ commit, state, dispatch }, { articlePrice, articleData, providerData, unitPrice, quantity, purchaseUnit }) {
			try {
				// SECURITY: Validate parameters
				if (!articleData || !articleData.ID) {
					throw new Error("Invalid article data");
				}
				if (!providerData || !providerData.ID) {
					throw new Error("Invalid provider data");
				}
				if (!unitPrice || typeof unitPrice !== 'number') {
					throw new Error("Invalid unit price");
				}
				if (!quantity || typeof quantity !== 'number') {
					throw new Error("Invalid quantity. Must be a non-zero number");
				}

				// Enriquecer con medida/referencia/precio anidado para replicar la
				// forma de los items del API (incidencias #1/#2/#3 ORDEN 38).
				const { topLevel, itemFields } = buildEnrichedItemFields({
					articlePrice,
					unitPrice,
					fallbackPurchaseUnit: purchaseUnit,
				});

				// Usar directamente los datos recibidos (ya vienen del API /articles)
				const newItem = {
					ID: Date.now(), // ID temporal para el item del carrito
					article_id: articleData.ID,
					ArticleID: articleData.ID,
					article_price_id: articlePrice?.ID || null,
					provider_id: providerData.ID,
					ProviderID: providerData.ID,
					unit_price: unitPrice,
					quantity: quantity,
					total_price: unitPrice * quantity,
					...topLevel,
					// Estructura anidada para compatibilidad
					item: {
						article: articleData.description || 'Artículo',
						provider: providerData.name,
						...itemFields
					},
					Article: {
						ID: articleData.ID,
						name: articleData.description,
						description: articleData.description,
						code: articleData.code
					},
					Provider: {
						ID: providerData.ID,
						name: providerData.name
					}
				};

				// Añadir al array de items
				const updatedItems = [...state.requestItems, newItem];

				// Actualizar requestItems en el estado
				commit("SET_REQUEST_ITEMS", updatedItems);

				// Re-agrupar por proveedor
				await dispatch("groupByProvider", updatedItems);

				return newItem;
			} catch (error) {
				// SECURITY: Handle auth errors
				if (error.response && (error.response.status === 401 || error.response.status === 403)) {
					window.location.href = "/login";
					throw new Error("Sesión expirada. Por favor, inicia sesión nuevamente.");
				}

				console.error("❌ Error adding item to cart:", error);
				throw error;
			}
		},

		// Eliminar un item del carrito
		async removeItem({ commit, state, dispatch }, itemId) {
			try {
				// Buscar el item en requestItems
				const itemIndex = state.requestItems.findIndex(item => item.ID === itemId);
				if (itemIndex === -1) {
					throw new Error("Item not found in cart");
				}

				// Clonar el array de items para mutación inmutable
				const updatedItems = [...state.requestItems];
				updatedItems.splice(itemIndex, 1); // Eliminar el item del array

				// Actualizar requestItems en el estado
				commit("SET_REQUEST_ITEMS", updatedItems);

				// Re-agrupar por proveedor
				await dispatch("groupByProvider", updatedItems);

				return true;
			} catch (error) {
				console.error("❌ Error removing item from cart:", error);
				throw error;
			}
		},
	},
};
